import detectBrowserRefresh from "common/utils/detectBrowserRefresh";
import { snakeCaseToTitleCase } from "common/utils/strings";
import { useEffect, useState } from "react";
import type { TabbedMenuItem } from "ui/components/TabbedMenu";

interface TabbedMenuInfo<T> {
  tab: T;
  setTab: (tab: T) => void;
  menuItems: TabbedMenuItem[];
}

interface TabbedMenuOptions<T extends string> {
  isSelected?: (currentTab: string, name: string) => boolean;
  prettyNames?: Record<T, string>;
}

// TODO: Support option to hide certain tabs based on condition and redirect to a default
// TODO: Support regular URLs in addition to current hash routes
export default function useTabbedMenu<T extends string>(
  tabNames: T[],
  options?: TabbedMenuOptions<T>,
): TabbedMenuInfo<T> {
  const [tab, setTab] = useState<T>(tabNames[0]);
  const hash = window.location.hash.substring(1) || undefined;
  const browserRefreshed = detectBrowserRefresh();

  const menuItems = tabNames.map((name) => ({
    text: options?.prettyNames?.[name] ?? snakeCaseToTitleCase(name),
    path: `#${name}`,
    selected: options?.isSelected ? options.isSelected(tab, name) : tab === name,
    onClick: () => setTab(name),
  }));

  const handleTabChange = () => {
    const tab = location.hash.substring(1) as T;
    setTab(tab || tabNames[0]);
  };

  // This will account for manual url changes and external source links (i.e. emailed link)
  useEffect(() => {
    if (browserRefreshed) {
      handleTabChange();
    }

    window.addEventListener("popstate", handleTabChange);

    return () => {
      window.removeEventListener("popstate", handleTabChange);
    };
  }, [hash, browserRefreshed]);

  return { tab, setTab, menuItems };
}
